import * as React from 'react';
import {
  CHANNEL_NAME,
  type ConnectedWorkspaceInfo,
  getConnectedWorkspaceInfo,
  getStreamLock,
  isLockStale,
  removeConnectedWorkspaceInfo,
  removeStreamLock,
  type SharedMessage,
  STREAM_LOCK_HEARTBEAT_MS,
  STREAM_LOCK_STALE_CHECK_MS,
  type StreamLock,
  safePostMessage,
  setConnectedWorkspaceInfo,
  setStreamLock,
  TAB_ID,
} from '@/lib/sharedState';

/**
 * Context value for cross-tab shared state.
 */
export interface SharedStateContextValue {
  tabId: string;

  // Stream lock state
  streamLock: StreamLock | null;
  hasStreamLock: boolean;
  isStreamLockedByOther: (workspaceId: string) => boolean;
  acquireStreamLock: (workspaceId: string) => boolean;
  forceAcquireStreamLock: (workspaceId: string) => void;
  releaseStreamLock: () => void;

  // Stream transfer
  pendingTransferRequest: { fromTabId: string; workspaceId: string } | null;
  requestStreamTransfer: (workspaceId: string) => void;
  acceptTransferRequest: () => void;
  denyTransferRequest: () => void;

  // Workspace sync
  connectedWorkspaceInfo: ConnectedWorkspaceInfo | null;
  broadcastWorkspaceConnected: (id: string, name: string) => void;
  broadcastWorkspaceDisconnected: () => void;

  // Notebook sync
  broadcastNotebookChange: (path: string, workspaceId: string) => void;
  onNotebookChangedExternally: (cb: (path: string, workspaceId: string) => void) => () => void;

  // Notebook cells sync (for execution updates across tabs)
  broadcastNotebookCellsUpdated: (path: string, workspaceId: string) => void;
  onNotebookCellsUpdatedExternally: (cb: (path: string, workspaceId: string) => void) => () => void;
}

const SharedStateContext = React.createContext<SharedStateContextValue | null>(null);

interface SharedStateProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for cross-tab state synchronization.
 *
 * Uses BroadcastChannel for real-time communication and localStorage
 * for persistent state like stream locks.
 */
export function SharedStateProvider({ children }: SharedStateProviderProps) {
  // BroadcastChannel ref
  const channelRef = React.useRef<BroadcastChannel | null>(null);

  // Stream lock state
  const [streamLock, setStreamLockState] = React.useState<StreamLock | null>(() => {
    const lock = getStreamLock();
    // Clear stale locks on init
    if (lock && isLockStale(lock)) {
      removeStreamLock();
      return null;
    }
    return lock;
  });

  // Connected workspace info
  const [connectedWorkspaceInfo, setConnectedWorkspaceInfoState] = React.useState<ConnectedWorkspaceInfo | null>(() =>
    getConnectedWorkspaceInfo()
  );

  // Pending transfer request (only set if this tab owns the stream)
  const [pendingTransferRequest, setPendingTransferRequest] = React.useState<{
    fromTabId: string;
    workspaceId: string;
  } | null>(null);

  // Notebook change callbacks
  const notebookChangeCallbacksRef = React.useRef<Set<(path: string, workspaceId: string) => void>>(new Set());

  // Notebook cells updated callbacks (for execution sync across tabs)
  const notebookCellsUpdatedCallbacksRef = React.useRef<Set<(path: string, workspaceId: string) => void>>(new Set());

  // Computed: does this tab own the stream lock?
  const hasStreamLock = React.useMemo(() => {
    if (!streamLock) return false;
    if (isLockStale(streamLock)) return false;
    return streamLock.tabId === TAB_ID;
  }, [streamLock]);

  // Check if stream is locked by another tab for a given workspace
  const isStreamLockedByOther = React.useCallback(
    (workspaceId: string): boolean => {
      if (!streamLock) return false;
      if (isLockStale(streamLock)) return false;
      if (streamLock.tabId === TAB_ID) return false;
      return streamLock.workspaceId === workspaceId;
    },
    [streamLock]
  );

  // Broadcast a message to other tabs. Guarded against the channel
  // being closed via safePostMessage: races between effect cleanup
  // (which closes the channel) and queued callbacks (heartbeat ticks,
  // message handlers, beforeunload, async state-setters) would
  // otherwise surface as InvalidStateError in Sentry.
  const broadcast = React.useCallback((message: SharedMessage) => {
    safePostMessage(channelRef.current, message);
  }, []);

  // Acquire stream lock for a workspace
  const acquireStreamLock = React.useCallback(
    (workspaceId: string): boolean => {
      const currentLock = getStreamLock();

      // Check if lock exists and is valid
      if (currentLock && !isLockStale(currentLock)) {
        // Already locked by this tab
        if (currentLock.tabId === TAB_ID) {
          // Update timestamp
          const newLock: StreamLock = { ...currentLock, timestamp: Date.now() };
          setStreamLock(newLock);
          setStreamLockState(newLock);
          return true;
        }
        // Locked by another tab
        return false;
      }

      // Acquire the lock
      const newLock: StreamLock = {
        tabId: TAB_ID,
        workspaceId,
        timestamp: Date.now(),
      };
      setStreamLock(newLock);
      setStreamLockState(newLock);
      broadcast({ type: 'STREAM_LOCK_ACQUIRED', tabId: TAB_ID, workspaceId });
      return true;
    },
    [broadcast]
  );

  // Forcibly take the stream lock from whichever tab currently holds it.
  // Unlike acquireStreamLock this overwrites a live lock owned by another
  // tab. The displaced tab receives STREAM_LOCK_ACQUIRED, flips to
  // "locked by other", and unmounts its AppStream via the render gate in
  // StreamOnlyWindow — tearing down its WebRTC peer so the two tabs never
  // stream at once. This is the escape hatch behind the overlay's "View
  // here anyway" button: the cooperative transfer handshake can't complete
  // when the owning tab is gone/unreachable, which is exactly when a user
  // is stuck staring at the locked overlay.
  const forceAcquireStreamLock = React.useCallback(
    (workspaceId: string): void => {
      const newLock: StreamLock = {
        tabId: TAB_ID,
        workspaceId,
        timestamp: Date.now(),
      };
      setStreamLock(newLock);
      setStreamLockState(newLock);
      broadcast({ type: 'STREAM_LOCK_ACQUIRED', tabId: TAB_ID, workspaceId });
    },
    [broadcast]
  );

  // Release stream lock
  const releaseStreamLock = React.useCallback(() => {
    const currentLock = getStreamLock();
    if (currentLock && currentLock.tabId === TAB_ID) {
      removeStreamLock();
      setStreamLockState(null);
      broadcast({ type: 'STREAM_LOCK_RELEASED', tabId: TAB_ID });
    }
  }, [broadcast]);

  // Request stream transfer from another tab
  const requestStreamTransfer = React.useCallback(
    (workspaceId: string) => {
      const currentLock = getStreamLock();
      if (!currentLock || currentLock.tabId === TAB_ID) return;

      broadcast({
        type: 'REQUEST_STREAM_TRANSFER',
        fromTabId: TAB_ID,
        toTabId: currentLock.tabId,
        workspaceId,
      });
    },
    [broadcast]
  );

  // Accept a pending transfer request
  const acceptTransferRequest = React.useCallback(() => {
    if (!pendingTransferRequest) return;

    // Release the lock
    releaseStreamLock();

    // Notify the requesting tab
    broadcast({ type: 'STREAM_TRANSFER_ACCEPTED', fromTabId: TAB_ID });
    setPendingTransferRequest(null);
  }, [pendingTransferRequest, releaseStreamLock, broadcast]);

  // Deny a pending transfer request
  const denyTransferRequest = React.useCallback(() => {
    if (!pendingTransferRequest) return;

    broadcast({ type: 'STREAM_TRANSFER_DENIED', fromTabId: TAB_ID });
    setPendingTransferRequest(null);
  }, [pendingTransferRequest, broadcast]);

  // Broadcast workspace connected
  const broadcastWorkspaceConnected = React.useCallback(
    (id: string, name: string) => {
      const info: ConnectedWorkspaceInfo = {
        id,
        name,
        tabId: TAB_ID,
        timestamp: Date.now(),
      };
      setConnectedWorkspaceInfo(info);
      setConnectedWorkspaceInfoState(info);
      broadcast({ type: 'WORKSPACE_CONNECTED', workspaceId: id, workspaceName: name, tabId: TAB_ID });
    },
    [broadcast]
  );

  // Broadcast workspace disconnected
  const broadcastWorkspaceDisconnected = React.useCallback(() => {
    const info = getConnectedWorkspaceInfo();
    // Only clear if this tab set it
    if (info && info.tabId === TAB_ID) {
      removeConnectedWorkspaceInfo();
      setConnectedWorkspaceInfoState(null);
    }
    broadcast({ type: 'WORKSPACE_DISCONNECTED', tabId: TAB_ID });
  }, [broadcast]);

  // Broadcast notebook change
  const broadcastNotebookChange = React.useCallback(
    (path: string, workspaceId: string) => {
      broadcast({ type: 'NOTEBOOK_CHANGED', path, workspaceId, tabId: TAB_ID });
    },
    [broadcast]
  );

  // Subscribe to notebook changes from other tabs
  const onNotebookChangedExternally = React.useCallback((cb: (path: string, workspaceId: string) => void) => {
    notebookChangeCallbacksRef.current.add(cb);
    return () => {
      notebookChangeCallbacksRef.current.delete(cb);
    };
  }, []);

  // Broadcast notebook cells updated (after execution)
  const broadcastNotebookCellsUpdated = React.useCallback(
    (path: string, workspaceId: string) => {
      broadcast({ type: 'NOTEBOOK_CELLS_UPDATED', path, workspaceId, tabId: TAB_ID });
    },
    [broadcast]
  );

  // Subscribe to notebook cells updates from other tabs
  const onNotebookCellsUpdatedExternally = React.useCallback((cb: (path: string, workspaceId: string) => void) => {
    notebookCellsUpdatedCallbacksRef.current.add(cb);
    return () => {
      notebookCellsUpdatedCallbacksRef.current.delete(cb);
    };
  }, []);

  // Handle incoming messages from other tabs
  const handleMessage = React.useCallback(
    (event: MessageEvent<SharedMessage>) => {
      const msg = event.data;

      switch (msg.type) {
        case 'STREAM_LOCK_ACQUIRED':
          // Another tab acquired the lock
          if (msg.tabId !== TAB_ID) {
            setStreamLockState({
              tabId: msg.tabId,
              workspaceId: msg.workspaceId,
              timestamp: Date.now(),
            });
          }
          break;

        case 'STREAM_LOCK_RELEASED':
          // Another tab released the lock
          if (msg.tabId !== TAB_ID) {
            const currentLock = getStreamLock();
            if (currentLock && currentLock.tabId === msg.tabId) {
              removeStreamLock();
              setStreamLockState(null);
            }
          }
          break;

        case 'REQUEST_STREAM_TRANSFER':
          // Another tab wants the stream
          if (msg.toTabId === TAB_ID && hasStreamLock) {
            setPendingTransferRequest({
              fromTabId: msg.fromTabId,
              workspaceId: msg.workspaceId,
            });
          }
          break;

        case 'STREAM_TRANSFER_ACCEPTED':
          // Transfer was accepted, we can now acquire the lock
          // The acquiring tab will call acquireStreamLock
          break;

        case 'STREAM_TRANSFER_DENIED':
          // Transfer was denied - handled by the component that requested
          break;

        case 'WORKSPACE_CONNECTED':
          if (msg.tabId !== TAB_ID) {
            setConnectedWorkspaceInfoState({
              id: msg.workspaceId,
              name: msg.workspaceName,
              tabId: msg.tabId,
              timestamp: Date.now(),
            });
          }
          break;

        case 'WORKSPACE_DISCONNECTED':
          if (msg.tabId !== TAB_ID) {
            const info = getConnectedWorkspaceInfo();
            if (info && info.tabId === msg.tabId) {
              setConnectedWorkspaceInfoState(null);
            }
          }
          break;

        case 'NOTEBOOK_CHANGED':
          if (msg.tabId !== TAB_ID) {
            for (const cb of notebookChangeCallbacksRef.current) {
              cb(msg.path, msg.workspaceId);
            }
          }
          break;

        case 'NOTEBOOK_CELLS_UPDATED':
          if (msg.tabId !== TAB_ID) {
            for (const cb of notebookCellsUpdatedCallbacksRef.current) {
              cb(msg.path, msg.workspaceId);
            }
          }
          break;

        case 'TAB_CLOSING':
          // Another tab is closing - clean up if it owned the lock
          if (msg.tabId !== TAB_ID) {
            const currentLock = getStreamLock();
            if (currentLock && currentLock.tabId === msg.tabId) {
              removeStreamLock();
              setStreamLockState(null);
            }
            const info = getConnectedWorkspaceInfo();
            if (info && info.tabId === msg.tabId) {
              removeConnectedWorkspaceInfo();
              setConnectedWorkspaceInfoState(null);
            }
          }
          break;
      }
    },
    [hasStreamLock]
  );

  // Keep a ref to the latest message handler so the BroadcastChannel
  // effect below can subscribe once on mount instead of tearing down and
  // re-creating the channel every time `handleMessage` (which depends on
  // `hasStreamLock`) changes identity. Re-subscribing on every state
  // change races with queued postMessage callers and surfaces as
  // "Failed to execute 'postMessage' on 'BroadcastChannel': Channel is
  // closed" in Sentry. useLayoutEffect (synchronous post-commit) closes
  // the stale-closure window that a passive useEffect would leave open
  // for queued onmessage tasks.
  const handleMessageRef = React.useRef(handleMessage);
  React.useLayoutEffect(() => {
    handleMessageRef.current = handleMessage;
  }, [handleMessage]);

  // Initialize BroadcastChannel and heartbeat (runs once on mount)
  React.useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;
    channel.onmessage = (event: MessageEvent<SharedMessage>) => {
      handleMessageRef.current(event);
    };

    // Load initial state from localStorage
    const savedLock = getStreamLock();
    if (savedLock && !isLockStale(savedLock)) {
      setStreamLockState(savedLock);
    } else if (savedLock) {
      // Clear stale lock
      removeStreamLock();
    }

    // Heartbeat to keep lock alive (runs on the lock-owning tab)
    const heartbeatInterval = setInterval(() => {
      const currentLock = getStreamLock();
      if (currentLock && currentLock.tabId === TAB_ID) {
        const updatedLock = { ...currentLock, timestamp: Date.now() };
        setStreamLock(updatedLock);
        setStreamLockState(updatedLock);
      }
    }, STREAM_LOCK_HEARTBEAT_MS);

    // Periodic staleness recheck (runs on non-owning tabs). If the
    // lock-owning tab crashed without firing beforeunload, neither
    // BroadcastChannel nor the storage event will clear the lock.
    // This interval re-reads localStorage and evicts stale locks.
    const staleCheckInterval = setInterval(() => {
      const currentLock = getStreamLock();
      if (currentLock && currentLock.tabId !== TAB_ID && isLockStale(currentLock)) {
        removeStreamLock();
        setStreamLockState(null);
      }
    }, STREAM_LOCK_STALE_CHECK_MS);

    // Cleanup on tab close. The safePostMessage helper swallows
    // InvalidStateError because the channel may already be closed when
    // the unload handler fires after React has run the effect cleanup
    // (e.g. during dev HMR or navigation interleaved with unmount).
    const handleUnload = () => {
      const currentLock = getStreamLock();
      if (currentLock && currentLock.tabId === TAB_ID) {
        removeStreamLock();
      }
      const info = getConnectedWorkspaceInfo();
      if (info && info.tabId === TAB_ID) {
        removeConnectedWorkspaceInfo();
      }
      safePostMessage(channelRef.current, { type: 'TAB_CLOSING', tabId: TAB_ID });
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(staleCheckInterval);
      window.removeEventListener('beforeunload', handleUnload);
      // Null the ref before closing so any in-flight broadcast() callers
      // see a missing channel instead of a closed one.
      channelRef.current = null;
      channel.onmessage = null;
      channel.close();
    };
  }, []);

  // Listen for localStorage changes from other tabs (fallback for BroadcastChannel)
  React.useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'antioch:stream:lock') {
        if (event.newValue) {
          try {
            const lock = JSON.parse(event.newValue) as StreamLock;
            setStreamLockState(lock);
          } catch {
            // Ignore parse errors
          }
        } else {
          setStreamLockState(null);
        }
      }
      if (event.key === 'antioch:workspace:connected') {
        if (event.newValue) {
          try {
            const info = JSON.parse(event.newValue) as ConnectedWorkspaceInfo;
            setConnectedWorkspaceInfoState(info);
          } catch {
            // Ignore parse errors
          }
        } else {
          setConnectedWorkspaceInfoState(null);
        }
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const value = React.useMemo<SharedStateContextValue>(
    () => ({
      tabId: TAB_ID,
      streamLock,
      hasStreamLock,
      isStreamLockedByOther,
      acquireStreamLock,
      forceAcquireStreamLock,
      releaseStreamLock,
      pendingTransferRequest,
      requestStreamTransfer,
      acceptTransferRequest,
      denyTransferRequest,
      connectedWorkspaceInfo,
      broadcastWorkspaceConnected,
      broadcastWorkspaceDisconnected,
      broadcastNotebookChange,
      onNotebookChangedExternally,
      broadcastNotebookCellsUpdated,
      onNotebookCellsUpdatedExternally,
    }),
    [
      streamLock,
      hasStreamLock,
      isStreamLockedByOther,
      acquireStreamLock,
      forceAcquireStreamLock,
      releaseStreamLock,
      pendingTransferRequest,
      requestStreamTransfer,
      acceptTransferRequest,
      denyTransferRequest,
      connectedWorkspaceInfo,
      broadcastWorkspaceConnected,
      broadcastWorkspaceDisconnected,
      broadcastNotebookChange,
      onNotebookChangedExternally,
      broadcastNotebookCellsUpdated,
      onNotebookCellsUpdatedExternally,
    ]
  );

  return <SharedStateContext.Provider value={value}>{children}</SharedStateContext.Provider>;
}

/**
 * Hook to access shared state context.
 */
export function useSharedState(): SharedStateContextValue {
  const context = React.useContext(SharedStateContext);
  if (!context) {
    throw new Error('useSharedState must be used within a SharedStateProvider');
  }
  return context;
}
